<script setup>
const props = defineProps({
  duration: {
    type: Number,
    default: 25,
  },
  activeColor: {
    type: String,
    default: '',
  },
  inactiveColor: {
    type: String,
    default: '',
  },
});
</script>
<template>
  <div
    class="container"
    :style="{
      '--duration': Math.round(props.duration), // Accounting for non-integer durations
      '--active-color': props.activeColor,
      '--inactive-color': props.inactiveColor,
    }"
  >
    <div class="timer"></div>
  </div>
</template>
<style scoped>
.container {
  --tick-mark-radial-start: 58%;
  --tick-mark-radial-end: 70%;

  /* This is to antialias the edges.
     Without this, the tick mark edged appear pixelated.
  */
  --radial-fade-size: 1px;

  /* If we didn't have to support Chrome < 120,
     we would be able to set two masks on the same element and use `mask-composite`.
     Granted, there turns out to be a `-webkit-mask-composite`, but that uses
     non-standard values, so using it seems to be not much easier than using the two masks
     on different elements.
  */
  --mask: radial-gradient(
    transparent calc(var(--tick-mark-radial-start) - var(--radial-fade-size)),
    black var(--tick-mark-radial-start),
    black var(--tick-mark-radial-end),
    transparent calc(var(--tick-mark-radial-end) + var(--radial-fade-size))
  );

  /* The unprefixed version only works in Chrome 120+, so this is to support Chrome 69+. */
  -webkit-mask-image: var(--mask);
  mask-image: var(--mask);
}

.timer {
  width: 100%;
  height: 100%;

  /* The ::before and ::after elements are absolutely positioned,
     so the container has to be relatively positioned */
  position: relative;

  /* The ::before and ::after elements extend outside the limits of this element,
     so we need to cut them off in order to not cause page size to grow.
  */
  overflow: hidden;

  /* Similar to the radial fade, this is used to antialias the edged.
     This value looks good in both latest Chrome and Chrome 69.
  */
  --conic-fade-size: 0.8deg;

  --tick-mark-thickness: 3deg;

  /* We need to rotate both rotating elements by a little bit to ensure
     that the first tick mark isn't cut off by one of them, due to the fade.
  */
  --initial-rotation: calc(var(--conic-fade-size) * 2);

  /* 1 for clockwise, -1 for counter-clockwise.
     We can later pass this as a prop to the component.
  */
  --timer-direction: -1;

  background-color: var(--active-color);

  --mask: repeating-conic-gradient(
    from calc(-1 * var(--tick-mark-thickness)),
    black 0deg,
    black var(--tick-mark-thickness),
    transparent calc(var(--tick-mark-thickness) + var(--conic-fade-size)),
    transparent calc(360deg / var(--duration) - var(--conic-fade-size)),
    black calc(360deg / var(--duration))
  );

  -webkit-mask-image: var(--mask);
  mask-image: var(--mask);

  /* - Flip horizontally if timer direction is counter-clockwise.
     - Rotate by a little bit to make the first tick mark's center match the 0deg mark.
  */
  transform: scaleX(var(--timer-direction)) rotate(calc(var(--tick-mark-thickness) / 2));
}

/* Two half-circles sitting on the left side if moving clockwise, or right side if moving counter-clockwise.
   It would be much nicer if we could simply give the timer a `conic-gradient` background
   and animate the property that determines the "clock arm" angle, for example:

   @property --clock-arm-angle {
     syntax: "<angle>";
     initial-value: 0deg;
     inherits: false;
   }

   @keyframes fill-circle {
     to {
       --clock-arm-angle: 360deg;
     }
   }

   .timer {
     background-image: conic-gradient(
       var(--active-color) var(--clock-arm-angle),
       var(--inactive-color) var(--clock-arm-angle)
    );
   }

   Unfortunately, `@property` is only supported starting with Chrome 85,
   and we can't animate custom properties without assigning a type to them
   (https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/At-rules/@property#animating_a_custom_property_value),
   so here we instead resort to having two overlapping half-circles.
   One of them (`::after`) sits on top and provides the actual animation.
   The other (`::before`) jumps to the other side of the circle in the middle of the animation
   and changes its color, such as the total animation appears as a smooth filling
   of the circle from 0deg to 360deg.
*/
.timer::before,
.timer::after {
  content: '';
  position: absolute;

  /* Make the rotating elements big enough,
     we'll cut them off anyway by the mask */
  left: -50%;
  width: 100%;
  top: -50%;
  height: 200%;

  transform-origin: center right;

  animation-iteration-count: 1;
  animation-fill-mode: forwards;
}

/* Mask element (on top) */
.timer::before {
  z-index: 1;
  background-color: var(--active-color);
  transform: rotate(var(--initial-rotation));
  animation-name: timer-mask;
  animation-timing-function: steps(1, end);
  animation-duration: calc(var(--duration) * 1s / 2);
}

/* Rotating element (underneath) */
.timer::after {
  background-color: var(--inactive-color);
  animation-name: timer-rotate;
  animation-timing-function: linear;
  animation-duration: calc(var(--duration) * 1s);
}

/* The gray half-circle rotates a full turn */
@keyframes timer-rotate {
  from {
    transform: rotate(var(--initial-rotation));
  }
  to {
    transform: rotate(calc(360deg + var(--initial-rotation)));
  }
}

/* Mask jumps to the other side halfway and turns gray */
@keyframes timer-mask {
  to {
    background-color: var(--inactive-color);
    transform: rotate(calc(180deg + var(--initial-rotation)));
  }
}
</style>
